/**
 * RDS API client for core object reads.
 * Calls RDS REST API when USE_RDS_FOR_CORE_OBJECTS is enabled.
 */

export interface RdsRecord {
  id: string;
  [key: string]: any;
}

const RDS_API_URL =
  process.env.REACT_APP_RDS_API_URL || process.env.RDS_API_URL || '';

function getAuthHeaders(): Record<string, string> {
  const token =
    typeof window !== 'undefined'
      ? (window as any).__GobodoAuthToken ?? localStorage.getItem('@GobodoAuthToken')
      : null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) headers['Authorization'] = token;
  return headers;
}

export async function getRdsRecords(
  objectType: string,
  options?: { page?: number; size?: number }
): Promise<RdsRecord[]> {
  const page = options?.page ?? 0;
  const size = options?.size ?? 25;
  const url = `${RDS_API_URL.replace(/\/$/, '')}/api/rds/objects/${encodeURIComponent(objectType)}/records?page=${page}&size=${size}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`RDS API ${res.status}: ${await res.text()}`);
  const data = await res.json();
  const items = Array.isArray(data) ? data : data?.records ?? data?.items ?? [];
  return items.map((r: any) => ({
    id: String(r.id ?? r.Id ?? r.id),
    ...r,
  }));
}

export async function getRdsRecordCount(objectType: string): Promise<number> {
  const url = `${RDS_API_URL.replace(/\/$/, '')}/api/rds/objects/${encodeURIComponent(objectType)}/records/count`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`RDS API ${res.status}: ${await res.text()}`);
  const data = await res.json();
  const count = data?.count ?? data?.total;
  return typeof count === 'number' ? Math.max(0, count) : Math.max(0, parseInt(String(count ?? 0), 10));
}

export async function getRdsRecordById(
  objectType: string,
  recordId: string
): Promise<RdsRecord | null> {
  const url = `${RDS_API_URL.replace(/\/$/, '')}/api/rds/objects/${encodeURIComponent(objectType)}/records/${encodeURIComponent(recordId)}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`RDS API ${res.status}: ${await res.text()}`);
  const r = await res.json();
  return r ? { id: String(r.id ?? r.Id ?? r.id), ...r } : null;
}
